import { useCallback, useEffect, useState } from 'react';

/**
 * Shared hook for property "favorites" (wishlist hearts).
 *
 * Backing store: localStorage key `property_favorites` — a JSON array of
 * property ids. All PropertyCard/PropertyDetail instances subscribe to the
 * same `favorites-updated` event so toggling a heart on one card updates
 * every other card showing the same property without a refresh.
 *
 * When we later add a real backend wishlist endpoint, we can swap the
 * getAll/setAll helpers without touching any consumer.
 */

const STORAGE_KEY = 'property_favorites';
const EVENT = 'favorites-updated';

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const writeAll = (ids) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(new Set(ids.map(String)))));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    // ignore quota / access errors — failure to persist shouldn't break the UI
  }
};

export const useFavoriteProperty = (propertyId) => {
  const key = propertyId != null ? String(propertyId) : null;
  const [isFavorite, setIsFavorite] = useState(() =>
    key ? readAll().includes(key) : false
  );

  useEffect(() => {
    if (!key) return undefined;
    const sync = () => setIsFavorite(readAll().includes(key));
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [key]);

  const toggle = useCallback(() => {
    if (!key) return false;
    const current = readAll();
    const next = current.includes(key)
      ? current.filter((id) => id !== key)
      : [...current, key];
    writeAll(next);
    return next.includes(key);
  }, [key]);

  return { isFavorite, toggle };
};

export default useFavoriteProperty;
